import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, Loader2, Clock, MapPin, Calendar 
} from 'lucide-react';
import { LazyImage } from '@/components/ui/lazy-image';
import { ChinaBreadcrumbs } from '@/components/china/ChinaBreadcrumbs';
import { ChinaEssentialsMiniNav } from '@/components/china/ChinaEssentialsMiniNav';
import { EmailCaptureCTA } from '@/components/china/EmailCaptureCTA';
import { useChinaContent } from '@/hooks/china/useChinaContent';

type ContentType = 'planning' | 'itinerary' | 'city' | 'resource';

interface ChinaListingProps {
  type: ContentType;
}

const typeConfig: Record<ContentType, {
  title: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  emptyMessage: string;
}> = {
  planning: {
    title: 'China Planning Guides',
    description: 'Everything you need to prepare for your China trip. Visas, apps, payments, connectivity, and more.',
    metaTitle: 'China Travel Planning | Visas, Apps & Tips | Trotting the World',
    metaDescription: 'Plan your China trip with our comprehensive guides. Learn about visas, payment apps, VPNs, SIM cards, and essential preparations.',
    emptyMessage: 'Planning guides coming soon! Tag posts with "China Planning" to see them here.',
  },
  itinerary: {
    title: 'China Itineraries',
    description: 'Day-by-day travel plans for exploring China. From weekend getaways to multi-week adventures.',
    metaTitle: 'China Travel Itineraries | Day-by-Day Plans | Trotting the World',
    metaDescription: 'Detailed China travel itineraries with day-by-day plans. Perfect for first-timers and seasoned travelers alike.',
    emptyMessage: 'Itineraries coming soon! Tag itineraries with "China" to see them here.',
  },
  city: {
    title: 'China City Guides',
    description: 'In-depth guides to the best cities and regions in China. Shanghai, Beijing, Xi\'an, Chengdu, and more.',
    metaTitle: 'China City Guides | Shanghai, Beijing & More | Trotting the World',
    metaDescription: 'Explore China\'s top destinations with our city guides. Local tips, attractions, food, and hidden gems.',
    emptyMessage: 'City guides coming soon! Tag posts with "China Cities" to see them here.',
  },
  resource: {
    title: 'China Travel Resources',
    description: 'Useful tools, apps, and resources to make your China trip easier.',
    metaTitle: 'China Travel Resources | Apps & Tools | Trotting the World',
    metaDescription: 'Essential resources for traveling in China. Best apps, booking platforms, and practical tools.',
    emptyMessage: 'Resources coming soon! Tag posts with "China Resources" to see them here.',
  },
};

export default function ChinaListing({ type }: ChinaListingProps) {
  const config = typeConfig[type];
  const isItinerary = type === 'itinerary';
  
  const { posts, itineraries, loading } = useChinaContent({ 
    contentType: type,
    limit: 20 
  });

  const content = isItinerary ? itineraries : posts;

  return (
    <>
      <Helmet>
        <title>{config.metaTitle}</title>
        <meta name="description" content={config.metaDescription} />
        <meta property="og:title" content={config.metaTitle} />
        <meta property="og:description" content={config.metaDescription} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/china/${type === 'itinerary' ? 'itineraries' : type}`} />
      </Helmet>

      <div className="container py-8 md:py-12">
        <ChinaBreadcrumbs items={[{ label: config.title }]} />

        {/* Mini Nav */}
        <ChinaEssentialsMiniNav className="mb-8" />

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{config.title}</h1>
          <p className="text-muted-foreground max-w-2xl">{config.description}</p>
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
          </div>
        ) : content.length > 0 ? (
          <div className={`grid gap-6 mb-12 ${
            isItinerary 
              ? 'grid-cols-1 md:grid-cols-2' 
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {isItinerary ? (
              // Itinerary cards
              itineraries.map((item) => (
                <Card key={item.id} className="overflow-hidden group hover:shadow-md transition-shadow">
                  <div className="aspect-video relative overflow-hidden">
                    <LazyImage
                      src={item.featured_image || 'https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b'}
                      alt={item.title}
                      className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-5">
                    <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h2>
                    <div className="flex flex-wrap gap-y-2 mb-3">
                      {item.destination && (
                        <div className="flex items-center text-sm text-muted-foreground mr-4">
                          <MapPin className="h-4 w-4 mr-1" />
                          <span>{item.destination}</span>
                        </div>
                      )}
                      {item.duration && (
                        <div className="flex items-center text-sm text-muted-foreground mr-4">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>{item.duration} days</span>
                        </div>
                      )}
                      {item.best_season && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>{item.best_season}</span>
                        </div>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-muted-foreground line-clamp-2">{item.description}</p>
                    )}
                  </CardContent>
                  <CardFooter className="pt-0 px-5 pb-5">
                    <Link 
                      to={`/itineraries/${item.slug}`} 
                      className="flex items-center text-primary hover:underline"
                    >
                      <span>View itinerary</span>
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </CardFooter>
                </Card>
              ))
            ) : (
              // Blog post cards
              posts.map((item) => (
                <Card key={item.id} className="overflow-hidden group hover:shadow-md transition-shadow">
                  <div className="aspect-video relative overflow-hidden">
                    <LazyImage
                      src={item.featured_image || 'https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b'}
                      alt={item.title}
                      className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-4">
                    {item.tags && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {item.tags.slice(0, 2).map(tag => (
                          <Badge key={tag.id} variant="secondary" className="text-xs">
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <h3 className="font-semibold line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    {item.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {item.excerpt}
                      </p>
                    )}
                    <Link 
                      to={`/blog/${item.slug}`}
                      className="inline-flex items-center mt-3 text-sm text-primary hover:underline"
                    >
                      Read more <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : (
          <Card className="mb-12">
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">{config.emptyMessage}</p>
            </CardContent>
          </Card>
        )}

        {/* Email Capture */}
        <EmailCaptureCTA 
          headline="Get More China Content"
          description="Subscribe for new guides, itineraries, and travel tips for China."
          source={`china-${type}`}
        />
      </div>
    </>
  );
}
